import React, { useState, useEffect, useCallback } from 'react';
import CustomNavbar from '../CustomNavbar';
import {
    FaChartBar, FaUsers, FaServer, FaSync, FaShieldAlt,
    FaDatabase, FaClock, FaQrcode, FaUserCheck, FaSignInAlt,
} from 'react-icons/fa';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
    CartesianGrid, Legend,
} from 'recharts';
import api from '../axiosSetup';
import moment from 'moment';
import cfg from '../../config';

const C = {
    yellow:  '#facc15',
    green:   '#4ade80',
    red:     '#f87171',
    blue:    '#60a5fa',
    purple:  '#a78bfa',
    orange:  '#fb923c',
    border:  '#2d3748',
    card:    '#1e293b',
    muted:   '#64748b',
    text:    '#e2e8f0',
    sub:     '#94a3b8',
};

const KpiCard = ({ icon: Icon, label, value, color = C.yellow, sub }) => (
    <div style={{
        background: C.card, border: `1px solid ${C.border}`, borderRadius: '0.75rem',
        padding: '1.1rem 1.3rem', flex: '1 1 140px', minWidth: 0,
    }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem' }}>
            <div style={{
                width: 36, height: 36, borderRadius: '9px', flexShrink: 0,
                background: `${color}1a`, border: `1.5px solid ${color}40`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color, fontSize: '1rem',
            }}>
                <Icon />
            </div>
            <span style={{ color: C.sub, fontSize: '0.82rem', lineHeight: 1.2 }}>{label}</span>
        </div>
        <div style={{ fontSize: '1.9rem', fontWeight: 700, color: '#fff', lineHeight: 1.1 }}>
            {value ?? <span style={{ color: C.muted }}>—</span>}
        </div>
        {sub && <div style={{ fontSize: '0.75rem', color: C.muted, marginTop: '0.25rem' }}>{sub}</div>}
    </div>
);

const SectionTitle = ({ icon: Icon, children, color = C.yellow, mt = true }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', marginBottom: '0.75rem', marginTop: mt ? '2rem' : 0 }}>
        <div style={{
            width: 30, height: 30, borderRadius: '7px', flexShrink: 0,
            background: `${color}1a`, border: `1.5px solid ${color}40`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color, fontSize: '0.9rem',
        }}>
            <Icon />
        </div>
        <h5 style={{ margin: 0, color: '#fff', fontWeight: 600, fontSize: '0.95rem' }}>{children}</h5>
    </div>
);

const StatRow = ({ icon: Icon, label, value }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.84rem', padding: '0.2rem 0' }}>
        <span style={{ color: C.sub, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <Icon style={{ fontSize: '0.8rem' }} /> {label}
        </span>
        <span style={{ color: C.text, fontWeight: 500 }}>{value}</span>
    </div>
);

const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div style={{
            background: '#1a2035', border: `1px solid ${C.border}`, borderRadius: '0.45rem',
            padding: '0.5rem 0.8rem', fontSize: '0.8rem', color: C.text,
        }}>
            <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{label}</div>
            {payload.map(p => (
                <div key={p.name} style={{ color: p.color }}>{p.name}: <strong>{p.value}</strong></div>
            ))}
        </div>
    );
};

const AdminAnalytics = () => {
    const [analytics, setAnalytics] = useState(null);
    const [system, setSystem]       = useState(null);
    const [days, setDays]           = useState(30);
    const [loading, setLoading]     = useState(true);
    const [error, setError]         = useState('');

    const load = useCallback(async (d) => {
        setLoading(true);
        setError('');
        try {
            const [a, s] = await Promise.all([
                api.get(`/api/admin/analytics?days=${d}`),
                api.get('/api/admin/systemstats'),
            ]);
            setAnalytics(a.data);
            setSystem(s.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Fehler beim Laden der Daten.');
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(days); }, [days, load]);

    const formatUptime = (s) => {
        const h = Math.floor(s / 3600);
        const m = Math.floor((s % 3600) / 60);
        return h > 24 ? `${Math.floor(h / 24)}d ${h % 24}h ${m}m` : `${h}h ${m}m`;
    };

    const totalLogins  = analytics?.loginActivity?.reduce((s, r) => s + (r.logins || 0), 0) ?? null;
    const totalQR      = analytics?.qrActivity?.reduce((s, r) => s + (r.scans || 0), 0) ?? null;
    const activePct    = analytics?.members?.total
        ? Math.round((analytics.members.active / analytics.members.total) * 100)
        : 0;
    const memPct = system
        ? Math.round((system.memory.heapUsedMB / system.memory.heapTotalMB) * 100)
        : 0;

    const loginChartData = analytics?.loginActivity?.map(r => ({
        date:         moment(r.date).format('DD.MM'),
        'Logins':     r.logins,
        'Unique User': r.uniqueUsers,
    })) ?? [];

    const qrChartData = analytics?.qrActivity?.map(r => ({
        date:    moment(r.date).format('DD.MM'),
        'Scans': r.scans,
    })) ?? [];

    const TABLE_LABELS = {
        tblmember:           cfg.memberTermPlural,
        tbl_refresh_tokens:  'Refresh-Tokens',
        tbl_qr_scan_log:     'QR-Scan-Logs',
        tbl_changelog:       'Changelog-Einträge',
        tblhaeser:           cfg.costumeTermPlural,
        tblmove:             cfg.eventTermPlural,
    };

    return (
        <>
        <CustomNavbar />
        <div className="container-fluid px-3 px-md-4 mt-4 pb-5" style={{ maxWidth: 1100 }}>
            <style>{`@keyframes aSpin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                        width: 44, height: 44, borderRadius: '12px',
                        background: 'rgba(250,204,21,0.12)', border: '1.5px solid rgba(250,204,21,0.3)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '1.3rem', color: C.yellow,
                    }}>
                        <FaChartBar />
                    </div>
                    <div>
                        <h2 style={{ margin: 0, fontWeight: 700, color: '#fff', fontSize: '1.4rem' }}>Analytik &amp; System</h2>
                        <div style={{ color: C.muted, fontSize: '0.8rem' }}>Nur für Administratoren</div>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                    {[7, 14, 30, 60, 90].map(d => (
                        <button key={d} onClick={() => setDays(d)} style={{
                            padding: '0.28rem 0.65rem', borderRadius: '0.4rem', fontSize: '0.8rem',
                            cursor: 'pointer', fontWeight: days === d ? 700 : 400,
                            background: days === d ? C.yellow : 'transparent',
                            color: days === d ? '#000' : C.sub,
                            border: `1px solid ${days === d ? C.yellow : C.border}`,
                            transition: 'all 0.15s',
                        }}>
                            {d}T
                        </button>
                    ))}
                    <button onClick={() => load(days)} title="Aktualisieren" style={{
                        marginLeft: '0.25rem', padding: '0.28rem 0.6rem', borderRadius: '0.4rem',
                        background: 'transparent', border: `1px solid ${C.border}`, color: C.sub,
                        cursor: 'pointer', fontSize: '0.9rem', lineHeight: 1,
                    }}>
                        <FaSync style={loading ? { animation: 'aSpin 0.8s linear infinite' } : {}} />
                    </button>
                </div>
            </div>

            {error && <div className="alert alert-danger py-2">{error}</div>}

            {/* KPI Row */}
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <KpiCard icon={FaUsers}     label={`${cfg.memberTermPlural} gesamt`} value={analytics?.members?.total} />
                <KpiCard icon={FaUserCheck} label="Aktiv"             value={analytics?.members?.active}  color={C.green}  sub={`${activePct}% der ${cfg.memberTermPlural}`} />
                <KpiCard icon={FaSignInAlt} label={`Logins (${days}T)`}   value={totalLogins}             color={C.blue} />
                <KpiCard icon={FaShieldAlt} label="Aktive Sessions"   value={analytics?.activeSessions}   color={C.purple} />
                <KpiCard icon={FaQrcode}    label={`QR-Scans (${days}T)`} value={totalQR}                 color={C.orange} />
            </div>

            {/* Login Chart */}
            <SectionTitle icon={FaSignInAlt} color={C.blue}>Logins pro Tag</SectionTitle>
            <div style={{ background: C.card, borderRadius: '0.75rem', border: `1px solid ${C.border}`, padding: '1rem 0.5rem 0.5rem' }}>
                {loginChartData.length ? (
                    <ResponsiveContainer width="100%" height={210}>
                        <BarChart data={loginChartData} margin={{ top: 4, right: 16, left: -10, bottom: 4 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                            <XAxis dataKey="date" tick={{ fill: C.muted, fontSize: 11 }} />
                            <YAxis tick={{ fill: C.muted, fontSize: 11 }} allowDecimals={false} />
                            <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                            <Legend wrapperStyle={{ fontSize: '0.78rem', color: C.sub }} />
                            <Bar dataKey="Logins"      fill={C.blue}   radius={[3, 3, 0, 0]} />
                            <Bar dataKey="Unique User" fill="#818cf8"  radius={[3, 3, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <div style={{ height: 210, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted }}>
                        {loading ? 'Laden…' : 'Keine Daten im gewählten Zeitraum'}
                    </div>
                )}
            </div>

            {/* QR Chart */}
            <SectionTitle icon={FaQrcode} color={C.orange}>QR-Scans pro Tag</SectionTitle>
            <div style={{ background: C.card, borderRadius: '0.75rem', border: `1px solid ${C.border}`, padding: '1rem 0.5rem 0.5rem' }}>
                {qrChartData.length ? (
                    <ResponsiveContainer width="100%" height={210}>
                        <BarChart data={qrChartData} margin={{ top: 4, right: 16, left: -10, bottom: 4 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                            <XAxis dataKey="date" tick={{ fill: C.muted, fontSize: 11 }} />
                            <YAxis tick={{ fill: C.muted, fontSize: 11 }} allowDecimals={false} />
                            <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                            <Bar dataKey="Scans" fill={C.orange} radius={[3, 3, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <div style={{ height: 210, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted }}>
                        {loading ? 'Laden…' : 'Keine Daten im gewählten Zeitraum'}
                    </div>
                )}
            </div>

            {/* Bottom row: Top Users + System */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginTop: '2rem' }}>
                {/* Top Logins */}
                <div>
                    <SectionTitle icon={FaUsers} mt={false}>Top Logins ({days}T)</SectionTitle>
                    <div style={{ background: C.card, borderRadius: '0.75rem', border: `1px solid ${C.border}`, overflow: 'hidden' }}>
                        {analytics?.topUsers?.length ? (
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                                <thead>
                                    <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                                        <th style={{ padding: '0.55rem 0.9rem', color: C.muted, fontWeight: 500, textAlign: 'left', width: 32 }}>#</th>
                                        <th style={{ padding: '0.55rem 0.9rem', color: C.muted, fontWeight: 500, textAlign: 'left' }}>Name</th>
                                        <th style={{ padding: '0.55rem 0.9rem', color: C.muted, fontWeight: 500, textAlign: 'right' }}>Logins</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {analytics.topUsers.map((u, i) => (
                                        <tr key={i} style={{ borderBottom: `1px solid ${C.border}22` }}>
                                            <td style={{ padding: '0.5rem 0.9rem', color: C.muted }}>{i + 1}</td>
                                            <td style={{ padding: '0.5rem 0.9rem', color: C.text }}>{u.firstName} {u.lastName}</td>
                                            <td style={{ padding: '0.5rem 0.9rem', color: C.blue, fontWeight: 600, textAlign: 'right' }}>{u.logins}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div style={{ padding: '2rem', textAlign: 'center', color: C.muted }}>
                                {loading ? 'Laden…' : 'Keine Daten'}
                            </div>
                        )}
                    </div>
                </div>

                {/* System Stats */}
                <div>
                    <SectionTitle icon={FaServer} color={C.purple} mt={false}>Systemstatus</SectionTitle>
                    <div style={{ background: C.card, borderRadius: '0.75rem', border: `1px solid ${C.border}`, padding: '1.1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                        <StatRow icon={FaClock}    label="Uptime"       value={system ? formatUptime(system.uptime) : '…'} />
                        <StatRow icon={FaServer}   label="Node.js"      value={system?.nodeVersion ?? '…'} />
                        <StatRow icon={FaDatabase} label="DB-Größe"     value={system ? `${system.dbSizeMB} MB` : '…'} />

                        {/* Memory bar */}
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem', fontSize: '0.82rem' }}>
                                <span style={{ color: C.sub }}>Heap-Speicher</span>
                                <span style={{ color: C.text }}>
                                    {system ? `${system.memory.heapUsedMB} / ${system.memory.heapTotalMB} MB` : '…'}
                                </span>
                            </div>
                            <div style={{ height: 6, background: '#2d3748', borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{
                                    height: '100%', borderRadius: 3,
                                    width: `${memPct}%`,
                                    background: memPct > 80 ? C.red : memPct > 60 ? C.orange : C.green,
                                    transition: 'width 0.4s',
                                }} />
                            </div>
                            <div style={{ fontSize: '0.74rem', color: C.muted, marginTop: '0.2rem' }}>{memPct}% genutzt</div>
                        </div>

                        {/* Table counts */}
                        {system?.tableCounts && (
                            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '0.65rem', marginTop: '0.25rem' }}>
                                <div style={{ color: C.muted, fontSize: '0.74rem', marginBottom: '0.4rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                    Datenbankeinträge
                                </div>
                                {Object.entries(system.tableCounts)
                                    .filter(([, v]) => v !== null)
                                    .map(([table, count]) => (
                                        <div key={table} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.18rem 0', fontSize: '0.82rem', borderBottom: `1px solid ${C.border}22` }}>
                                            <span style={{ color: C.muted }}>{TABLE_LABELS[table] ?? table}</span>
                                            <span style={{ color: C.text, fontWeight: 500 }}>{count?.toLocaleString('de-DE')}</span>
                                        </div>
                                    ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

export default AdminAnalytics;
